import React, { useEffect, useMemo, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { FulfillmentDetailsDrawer } from '../common/FulfillmentDrawer';
import {
  ActiveOrdersTablePresentational,
  ActiveOrdersRequestPayload
} from '../ActiveOrdersTablePresentational/ActiveOrdersTablePresentational';
import { FacetGroup, FacetSelections, SegmentedFacetOption } from '../common/types';
import { useActiveOrdersQuery } from '../../data/useActiveOrdersQuery';
import {
  ACTIVE_ORDERS_DATE_SENTINEL,
  ActiveOrdersQueryVariables,
  ActiveOrdersRequestFilter
} from '../../data/activeOrdersDataModel';
import { buildActiveOrdersDateRangePresets, buildDefaultActiveOrdersDateRange } from '../../data/dateRangePresets';
import {
  EMPTY_ACTIVE_ORDER_FILTER_OPTIONS,
  useActiveOrderFilterOptionsQuery
} from '../../data/useActiveOrderFilterOptionsQuery';
import { getActiveOrderKey, mapActiveDistributionToRow } from '../../data/adapters/activeOrdersToRow';
import {
  buildActiveOrderDetailsParams,
  mergeDistributionWithOrderDetails
} from '../../data/adapters/fulfillmentDrawer';
import { useOrderDetailsQuery } from '../../data/useOrderDetailsQuery';
import { useDeliveryTrackerQuery } from '../../data/useDeliveryTrackerQuery';
import { isDeliveryTrackerEligible, buildDeliveryTrackerRequest } from '../../data/adapters/deliveryTracker';
import { RawFulfillmentGroup } from '../../data/types';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

// Pagination is client-side: one 'ALL' request fetches every active distribution (both types) so
// the Deliveries/Pickups toggle can filter and page locally. Unconfirmed that the API honors a
// page size this large (ADR-003). Discussing with product stated 200 should be sufficient.
const FETCH_ALL_PAGE_SIZE = 200;

// Keyed by the Deliveries/Pickups toggle's fulfillmentType (see requestPayload below) — previously
// a single hardcoded string, so a Delivery-tab customer saw "Pickup by" on the date-range control
// (OOP-4135).
const DATE_RANGE_LABEL: Record<'PICKUP' | 'DELIVERY', string> = {
  PICKUP: 'Pickup by',
  DELIVERY: 'Delivery Date'
};

// Single source of truth for the demo identity: docs/chapters/active-orders-table's chapter-mocks.ts
// and examples/Example.tsx (plus its error/empty variants) pass these into customerAccountId/
// purchaserId, keeping every consumer of this fixture identity in sync.
export const MOCK_CUSTOMER_ACCOUNT_ID = 'mock-account-1';
export const MOCK_PURCHASER_ID = 'mock-purchaser-1';

export interface ActiveOrdersTableProps {
  /**
   * Identifies the signed-in customer for the activeOrders query. In production, omit — sourced
   * automatically from useThdCustomer() (@thd-olt-functional/customer-information), same as
   * AllOrdersTable. Pass explicitly only to override (e.g. docs/tests); an explicit value always
   * wins over useThdCustomer().
   */
  customerAccountId?: string;
  /**
   * Mirrors AllOrdersTable's userId -> useThdCustomer().userID mapping. Flagged in ADR-003 as an
   * unconfirmed assumption, since activeOrders' request argument is named purchaserId, not userId.
   */
  purchaserId?: string;
  /** Forwarded as request headers, matching AllOrdersTable's own convention. */
  sessionId?: string;
  channelId?: string;
  /** Page-size choices offered by the pagination bar's "N / Page" dropdown. Defaults to [10, 25, 50]; an explicit value overrides the default entirely. */
  pageSizeOptions?: number[];
  initialPageSize?: number;
  /**
   * Called when the account's overall active-order count (totalCardCount) is available or
   * changes — the full delivery+pickup total, not scoped to the current tab or page. Not called
   * while the query is loading/erroring.
   */
  onTotalOrdersChange?: (totalOrders: number) => void;
  /** Forwarded as-is to the fulfillment drawer's `isMobile` prop (see src/components/common/FulfillmentDrawer) — the drawer has no way to derive this itself. */
  isMobile?: boolean;
}

/**
 * Active Orders, connected to the activeOrders GraphQL API — see ADR-003.
 *
 * Unlike AllOrdersTable's purchaseHistory/orderDetails split, activeDistributions[] already
 * includes everything a row needs inline (recipient/location/instructions/lineItems) — no second
 * per-row query, so this wrapper has no row-enrichment machinery (no useRowsInView/
 * OrderDetailsFetcher/ExpandedOrderDetailsPanel equivalents).
 *
 * Composes the internal, non-exported ActiveOrdersTablePresentational shell for all rendering
 * (columns, toolbar, pagination).
 */
export function ActiveOrdersTable({
  customerAccountId: customerAccountIdProp,
  purchaserId: purchaserIdProp,
  sessionId,
  channelId,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  initialPageSize = DEFAULT_PAGE_SIZE,
  onTotalOrdersChange,
  isMobile
}: ActiveOrdersTableProps) {
  const thdCustomer = useThdCustomer();
  const customerAccountId = customerAccountIdProp ?? thdCustomer?.svocID ?? '';
  const purchaserId = purchaserIdProp ?? thdCustomer?.userID ?? '';
  const hasCustomerIdentity = Boolean(customerAccountId && purchaserId);

  // "Pickup by" presets/default are built into ActiveOrdersTable itself, not host-configurable
  // via props — mirrors AllOrdersTable's "Date Placed" treatment (ADR-002/ADR-003).
  const dateRangePresets = useMemo(() => buildActiveOrdersDateRangePresets(), []);

  // ActiveOrdersTablePresentational only shows the Deliveries/Pickups toggle when
  // fulfillmentFilterValue is truthy (a two-tab view, not an optional filter) — so this must
  // default to one of the two real option keys, matching the real "Active Deliveries & Pickups"
  // experience's default tab (Deliveries), not an empty value.
  const [requestPayload, setRequestPayload] = useState<ActiveOrdersRequestPayload>({
    // search: '', cannot be used until backend contract supports it
    dateRange: buildDefaultActiveOrdersDateRange(),
    facets: {},
    fulfillmentType: 'DELIVERY',
    page: 1,
    pageSize: initialPageSize
  });

  // Two contract-defined entries (distributionType, distributionDate) plus one {name, value} entry
  // per facet selection. Filter shape is singular {name, value}, not purchaseHistory's {name,
  // values: []} — a multi-value facet emits one entry per value (unconfirmed against the live
  // schema, see ADR-003).
  //
  // distributionDate carries the date window — ActiveOrdersRequest has no startDate/endDate fields
  // (confirmed 2026-08-12; an earlier revision wrongly sent dates). Its value must be exactly
  // 'ALL' | 'Today' | 'Tomorrow' | 'Next 7 Days', so the internal preset slug is translated through
  // ACTIVE_ORDERS_DATE_SENTINEL rather than sent raw — 'next7Days' isn't a recognized value.
  //
  // Because the API can't express an arbitrary range, ActiveOrdersTablePresentational renders
  // DateRangeFilter with allowCustomRange={false} — only the four presets (All, Today, Tomorrow, Next 7 Days) are selectable; All is the default. A missing
  // or unmapped preset still falls back to 'ALL' (widest window) as a defensive default (ADR-003).
  //
  // distributionType is always 'ALL': the Deliveries/Pickups toggle is a client-side view over one
  // combined response (rows are filtered by distributionType below), so switching tabs never
  // refetches.
  const filters = useMemo<ActiveOrdersRequestFilter[]>(() => {
    const facetFilters = Object.entries(requestPayload.facets).flatMap(([name, values]) =>
      values.map((value) => ({ name, value }))
    );
    const preset = requestPayload.dateRange.preset;
    return [
      { name: 'distributionType', value: 'ALL' },
      { name: 'distributionDate', value: (preset && ACTIVE_ORDERS_DATE_SENTINEL[preset]) || 'ALL' },
      ...facetFilters
    ];
  }, [requestPayload.facets, requestPayload.dateRange.preset]);

  // No startDate/endDate here: not fields on ActiveOrdersRequest (confirmed 2026-08-12) — the date
  // window travels entirely through the distributionDate filter sentinel built above.
  //
  // searchValue/sortBy/sortOrder are deliberately NOT sent (2026-09-21): order-management-svc's
  // schema.graphqls confirms `ActiveOrdersRequest` has no such fields — including them fails the
  // whole request with a variable error, the failure mode activeOrdersDataModel.ts's header comment
  // warned about. The search box is therefore not wired here (no searchValue/onSearchSubmit passed
  // to ActiveOrdersTablePresentational) until a real backend contract exists. Sorting was removed
  // outright, not just unwired: ActiveOrdersTablePresentational no longer accepts
  // sortBy/sortDirection/onSortChange, and its columns no longer mark themselves sortable.
  //
  // A top-level `userId` IS required despite order-management-svc's checked-in schema.graphqls not
  // declaring it as an `activeOrders` argument (confirmed on `develop`, 2026-09-22 — see
  // concerns.instructions.md C6): a request without it fails, confirmed against the live API
  // (2026-09-22). Treat the SDL as out of sync with the deployed contract for this argument, not as
  // ground truth to "fix" back out. Sent as `purchaserId` (same resolved identity as
  // `request.purchaserId` above), not `thdCustomer?.userID` directly — a working sample request has
  // `userId` exactly equal to `request.purchaserId`, and reading `thdCustomer?.userID` here would
  // silently diverge whenever an explicit `purchaserId` prop overrides `useThdCustomer()`.
  const activeOrdersVariables = useMemo<ActiveOrdersQueryVariables>(
    () => ({
      request: {
        customerAccountId,
        purchaserId,
        pageSize: FETCH_ALL_PAGE_SIZE,
        pageNumber: 1,
        filters
      },
      headers: { tmxProfileId: sessionId, channelId },
      userId: purchaserId
    }),
    [customerAccountId, purchaserId, filters, sessionId, channelId]
  );

  const activeOrdersResult = useActiveOrdersQuery(activeOrdersVariables, { skip: !hasCustomerIdentity });
  const rawDistributions = useMemo(
    () => (activeOrdersResult.status === 'success' ? (activeOrdersResult.data.activeDistributions ?? []) : []),
    [activeOrdersResult]
  );

  // The response holds both distribution types; the toggle selects which subset to show.
  const tabDistributions = useMemo(
    () => rawDistributions.filter((distribution) => distribution.distributionType === requestPayload.fulfillmentType),
    [rawDistributions, requestPayload.fulfillmentType]
  );

  // Client-side pagination: the total is the number of loaded distributions on the active tab.
  const totalItems = tabDistributions.length;

  // Live counts from the query result drive the Deliveries/Pickups toggle instead of a static
  // mock. Per product direction, both counts must always show the account's real full totals — a
  // user on the Deliveries tab must still see the true Pickups count, not 0 — so this relies on
  // totalDeliveryCount/totalPickupCount being stable full totals regardless of the requested
  // distributionType filter, not counts scoped to it. Unconfirmed against the live schema
  // (ADR-003) — if the real API scopes these to the requested filter, the toggle will incorrectly
  // show 0 for the inactive tab and needs revisiting (e.g. a second, always-'ALL' counts-only
  // request).
  // totalCardCount has no UI slot today — surfaced only via onTotalOrdersChange (ADR-003).
  useEffect(() => {
    if (activeOrdersResult.status === 'success') {
      onTotalOrdersChange?.(activeOrdersResult.data.totalCardCount ?? 0);
    }
  }, [activeOrdersResult, onTotalOrdersChange]);

  const fulfillmentFilterOptions = useMemo<SegmentedFacetOption[]>(() => {
    if (activeOrdersResult.status !== 'success') {
      return [];
    }
    return [
      { key: 'DELIVERY', label: 'Deliveries', count: activeOrdersResult.data.totalDeliveryCount ?? 0 },
      { key: 'PICKUP', label: 'Pickups', count: activeOrdersResult.data.totalPickupCount ?? 0 }
    ];
  }, [activeOrdersResult]);

  const pageDistributions = useMemo(() => {
    const start = (requestPayload.page - 1) * requestPayload.pageSize;
    return tabDistributions.slice(start, start + requestPayload.pageSize);
  }, [tabDistributions, requestPayload.page, requestPayload.pageSize]);
  const rows = useMemo(() => pageDistributions.map(mapActiveDistributionToRow), [pageDistributions]);

  // Keyed the same way as ActiveOrdersTablePresentational's PurchaseHistoryTable `rowKey`
  // (`${orderNumber}-${distributionType}`, see getActiveOrderKey) so a clicked row's key resolves
  // back to the RAW distribution the fulfillment drawer needs — the mapped PurchaseHistoryRow has
  // already dropped fields (e.g. instructions) the drawer may want.
  const distributionsByKey = useMemo(
    () => new Map(pageDistributions.map((distribution) => [getActiveOrderKey(distribution), distribution])),
    [pageDistributions]
  );
  const [openOrderKey, setOpenOrderKey] = useState<string | undefined>(undefined);
  const selectedDistribution = openOrderKey ? distributionsByKey.get(openOrderKey) : undefined;

  // Only built (and fetched) once the drawer is open for a given order — mirrors this repo's
  // "fetch orderDetails only for what's needed" discipline (elsewhere bounded by scroll-into-view;
  // here by the drawer being open). Returns null when the selected distribution lacks enough
  // identifying data for either orderDetails variant (see buildActiveOrderDetailsParams), in which
  // case the query below is skipped.
  const orderDetailsParams = selectedDistribution
    ? buildActiveOrderDetailsParams(selectedDistribution, {
        userId: purchaserId,
        customerAccountId,
        tmxProfileId: sessionId,
        channelId
      })
    : null;
  const orderDetailsResult = useOrderDetailsQuery(hasCustomerIdentity ? orderDetailsParams : null);
  const fulfillmentGroup = selectedDistribution
    ? mergeDistributionWithOrderDetails(
        selectedDistribution,
        orderDetailsResult.status === 'success' ? orderDetailsResult.data : undefined
      )
    : undefined;

  // Delivery-tracker status/links are NOT part of orderDetails (see orderDetailsDataModel.ts and
  // types.ts's RawFulfillmentGroup header comments) — they come from the SEPARATE deliveryTracker
  // REST endpoint, same as AllOrdersTable's ExpandedOrderDetailsPanel. `trackingNumber`/
  // `workOrderNumber` are read off the raw distribution (existing RawActiveDistribution fields,
  // unrelated to OOP-4197); `fulfillmentType`/`lineItems[0].statusDescription` come from the merged
  // fulfillmentGroup above, so eligibility stays false until orderDetails resolves a
  // fulfillmentType.
  const deliveryTrackerEligibilityGroup: RawFulfillmentGroup = {
    fulfillmentType: fulfillmentGroup?.fulfillmentType,
    trackingNumber: selectedDistribution?.trackingNumber,
    workOrderNumber: selectedDistribution?.workOrderNumber,
    lineItems: fulfillmentGroup?.lineItems?.length
      ? [{ statusDescription: fulfillmentGroup.lineItems[0]?.statusDescription }]
      : undefined
  };
  const isTrackerEligible = isDeliveryTrackerEligible(deliveryTrackerEligibilityGroup);
  const deliveryTrackerRequest =
    isTrackerEligible && selectedDistribution
      ? buildDeliveryTrackerRequest(
          deliveryTrackerEligibilityGroup,
          selectedDistribution.orderNumber ?? '',
          customerAccountId
        )
      : null;
  const deliveryTrackerResult = useDeliveryTrackerQuery(purchaserId, deliveryTrackerRequest, {
    skip: !selectedDistribution || !isTrackerEligible
  });
  const fulfillmentGroupWithTracker = fulfillmentGroup
    ? {
        ...fulfillmentGroup,
        deliveryTrackerData: deliveryTrackerResult.status === 'success' ? deliveryTrackerResult.data : undefined
      }
    : undefined;

  // Project/PO-Job Name facet options come from their own dedicated API — see
  // useActiveOrderFilterOptionsQuery — never derived from activeOrders' already-loaded rows.
  const filterOptionsResult = useActiveOrderFilterOptionsQuery(
    { purchaserId, customerAccountId },
    { skip: !hasCustomerIdentity }
  );
  const filterOptions =
    filterOptionsResult.status === 'success' ? filterOptionsResult.data : EMPTY_ACTIVE_ORDER_FILTER_OPTIONS;

  const facetGroupsFromApi = useMemo<FacetGroup[]>(
    () =>
      hasCustomerIdentity
        ? [
            {
              key: 'project',
              title: 'Project',
              options: filterOptions.project.map((name) => ({ label: name, value: name })),
              selectedValues: []
            },
            {
              key: 'poJobName',
              title: 'PO/Job Name',
              options: filterOptions.poJobName.map((name) => ({ label: name, value: name })),
              selectedValues: []
            }
          ]
        : [],
    [hasCustomerIdentity, filterOptions]
  );
  const facetGroupsWithSelections = useMemo(
    () =>
      facetGroupsFromApi.map((group) => ({
        ...group,
        selectedValues: requestPayload.facets[group.key] ?? group.selectedValues
      })),
    [facetGroupsFromApi, requestPayload.facets]
  );

  const handleFacetsApply = (facets: FacetSelections) =>
    setRequestPayload((previous) => ({ ...previous, facets, page: 1 }));

  return (
    <>
      <ActiveOrdersTablePresentational
        rows={rows}
        onDateCellClick={setOpenOrderKey}
        openOrderKey={openOrderKey}
        isLoading={activeOrdersResult.status === 'loading' || activeOrdersResult.status === 'idle'}
        isError={activeOrdersResult.status === 'error'}
        onRetry={activeOrdersResult.status === 'error' ? activeOrdersResult.refetch : undefined}
        dateRangeLabel={DATE_RANGE_LABEL[requestPayload.fulfillmentType as 'PICKUP' | 'DELIVERY'] ?? DATE_RANGE_LABEL.DELIVERY}
        dateRangeValue={requestPayload.dateRange}
        dateRangePresets={dateRangePresets}
        onDateRangeApply={(dateRange) => setRequestPayload((previous) => ({ ...previous, dateRange, page: 1 }))}
        facetGroups={facetGroupsWithSelections}
        onFacetsApply={handleFacetsApply}
        isFacetsLoading={filterOptionsResult.status === 'loading'}
        facetsError={filterOptionsResult.status === 'error'}
        fulfillmentFilterOptions={fulfillmentFilterOptions}
        fulfillmentFilterValue={requestPayload.fulfillmentType}
        onFulfillmentFilterChange={(fulfillmentType) =>
          setRequestPayload((previous) => ({ ...previous, fulfillmentType, page: 1 }))
        }
        totalItems={totalItems}
        page={requestPayload.page}
        pageSize={requestPayload.pageSize}
        onPageChange={(page) => setRequestPayload((previous) => ({ ...previous, page }))}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={(pageSize) => setRequestPayload((previous) => ({ ...previous, pageSize, page: 1 }))}
      />
      {/*
        FulfillmentDetailsDrawer (src/components/common/FulfillmentDrawer — a one-to-one local port
        of the active-orders-carousel effort's drawer, see OOP-4059 and that directory's doc
        comment for why it's a port, not a package import) is purely presentational and expects a
        flat `fulfillmentGroup` object.

        `fulfillmentGroupWithTracker` (OOP-4197) is built in two steps: (1)
        `mergeDistributionWithOrderDetails` overlays the raw activeOrders distribution with a
        second, drawer-only orderDetails query (useOrderDetailsQuery) fired only while the drawer
        is open — see src/data/adapters/fulfillmentDrawer.ts; (2) a THIRD, separate REST call
        (useDeliveryTrackerQuery, same endpoint AllOrdersTable's ExpandedOrderDetailsPanel uses)
        supplies `deliveryTrackerData` — deliberately not part of orderDetails (see
        orderDetailsDataModel.ts). Together these close the drawer's long-standing gap
        (fulfillmentType/storePickup/bodfsDeliveryAndHandling/shippingAndHandling/
        lineItems[].statusCode/deliveryTrackerData were never in the activeOrders response) for
        ONLINE orders. KNOWN REMAINING GAP: storeNumber/transactionId/registerNumber/salesDate/
        orderOrigin were all CONFIRMED REJECTED by the real stage schema (removed from
        activeOrdersDataModel.ts's selection — see that file's and types.ts's RawActiveDistribution
        header comments) — a real distribution never carries them, so
        buildActiveOrderDetailsParams' 'inStore' variant is effectively unreachable in production;
        the drawer falls back to the raw distribution's own fields for in-store/pickup orders,
        same as before this integration. A failed/loading/trimmed-shape orderDetails or
        deliveryTracker result degrades the same way, never blocking the drawer itself.
      */}
      <FulfillmentDetailsDrawer
        open={Boolean(selectedDistribution)}
        onClose={() => setOpenOrderKey(undefined)}
        fulfillmentGroup={fulfillmentGroupWithTracker}
        isMobile={isMobile}
        isLoading={orderDetailsResult.status === 'loading'}
      />
    </>
  );
}
